import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Trash2,
  Search,
  X,
  Mail,
  User,
  Edit2,
  Save,
  Loader2,
  UserPlus,
  BookUser
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { contactsApi } from '../../api/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export default function ContactsManager({ 
  isOpen, 
  onClose, 
  onSelectContact 
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState({ email: '', name: '', notes: '' });
  const [adding, setAdding] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Load contacts on open
  useEffect(() => {
    if (isOpen) {
      loadContacts();
    }
  }, [isOpen]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const data = await contactsApi.getContacts();
      setContacts(data || []);
    } catch (err) {
      console.error('Failed to load contacts:', err);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Search for users to add
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const results = await contactsApi.search(query, 10);
      // Filter out existing contacts
      const filtered = results.filter(
        r => !contacts.some(c => c.email === r.email)
      );
      setSearchResults(filtered);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddContact = async (email, name) => {
    setAdding(true);
    try {
      const contact = await contactsApi.addContact({ 
        email, 
        name: name || email.split('@')[0] 
      });
      setContacts(prev => [...prev, contact].sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      ));
      setNewContact({ email: '', name: '', notes: '' });
      setShowAddForm(false);
      setSearchQuery('');
      setSearchResults([]);
      toast.success('Contact added!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add contact');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveContact = async (contactId, contactName) => {
    if (!window.confirm(`Remove ${contactName} from contacts?`)) return;
    
    try {
      await contactsApi.removeContact(contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
      toast.success('Contact removed');
    } catch (err) {
      toast.error('Failed to remove contact');
    }
  };

  const handleSelectContact = (contact) => {
    if (onSelectContact) {
      onSelectContact(contact);
      onClose();
    }
  };

  // Filter contacts by search
  const filteredContacts = contacts.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.email?.toLowerCase().includes(query) ||
      c.name?.toLowerCase().includes(query)
    );
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <BookUser className="w-5 h-5 text-[#6D4AFF]" />
            My Contacts
          </DialogTitle>
        </DialogHeader>

        {/* Search & Add Section */}
        <div className="space-y-3 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search contacts or find users to add..."
              className="pl-9 bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Search Results - Users to Add */}
          {searchResults.length > 0 && (
            <div className="bg-[#252540] rounded-lg border border-[#2d2d44] overflow-hidden">
              <div className="px-3 py-2 text-xs text-gray-500 border-b border-[#2d2d44] flex items-center gap-1">
                <UserPlus className="w-3 h-3" />
                Add to Contacts
              </div>
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-[#2d2d44] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <span className="text-white text-xs">
                        {(user.display_name || user.email)?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-white">{user.display_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAddContact(user.email, user.display_name)}
                    disabled={adding}
                    className="bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white h-7 px-2"
                  >
                    {adding ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Plus className="w-3 h-3" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Manual Add Button */}
          {!showAddForm && searchResults.length === 0 && (
            <Button
              variant="outline"
              onClick={() => setShowAddForm(true)}
              className="w-full border-dashed border-[#2d2d44] text-gray-400 hover:text-white hover:bg-[#252540]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact Manually
            </Button>
          )}

          {/* Manual Add Form */}
          {showAddForm && (
            <div className="bg-[#252540] rounded-lg border border-[#2d2d44] p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white font-medium">Add New Contact</span>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewContact({ email: '', name: '', notes: '' });
                  }}
                  className="text-gray-500 hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={newContact.email}
                    onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="username"
                    className="flex-1 bg-[#1a1a2e] border-[#2d2d44] text-white"
                  />
                  <span className="text-gray-500 text-sm">@mypatriotmail.org</span>
                </div>
                <Input
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Display name (optional)"
                  className="bg-[#1a1a2e] border-[#2d2d44] text-white"
                />
              </div>
              <Button
                onClick={() => {
                  const email = newContact.email.includes('@') 
                    ? newContact.email 
                    : `${newContact.email}@mypatriotmail.org`;
                  handleAddContact(email, newContact.name);
                }}
                disabled={!newContact.email || adding}
                className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9]"
              >
                {adding ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Contact
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Contacts List */}
        <div className="flex-1 min-h-0 mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">
              {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#6D4AFF] animate-spin" />
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">
                {searchQuery ? 'No contacts match your search' : 'No contacts yet'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Search for users above to add them
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-1 pr-2">
                {filteredContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="group flex items-center justify-between p-2 rounded-lg hover:bg-[#252540] transition-colors"
                  >
                    <button
                      onClick={() => handleSelectContact(contact)}
                      className="flex items-center gap-3 flex-1 text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm font-medium">
                          {(contact.name || contact.email)?.[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">
                          {contact.name || contact.email?.split('@')[0]}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveContact(contact.id, contact.name || contact.email);
                        }}
                        className="h-8 w-8 text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 pt-3 border-t border-[#2d2d44]">
          <p className="text-xs text-gray-500 text-center">
            Click a contact to use their email • Hover to delete
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
